import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Save, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import VehicleForm, { validateVehicleForm, saveVehicleLog } from '@/components/VehicleForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function NewPayment() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [stores, setStores] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [vehicleNits, setVehicleNits] = useState([]);
  const [vehicleData, setVehicleData] = useState({
    destino_origen: '', destino_destino: '', placas: '', conductor: '',
    km_inicial: null, km_final: null, tanque_restante_litros: null,
    danos_reportados: false, danos_descripcion: '', danos_fotos_ids: [],
    pasajeros_reportados: false, pasajeros: [],
  });
  // Colaboradores con tienda asignada quedan atados a esa tienda
  const isRestrictedToStore = user?.role === 'colaborador' && !!user?.tienda_id;
  const [formData, setFormData] = useState({
    nombre_envia: '',
    proveedor_id: '',
    tienda_id: isRestrictedToStore ? user.tienda_id : '',
    numero_cuenta: '',
    titular_cuenta: '',
    banco: '',
    tipo_cuenta: '',
    monto_depositar: '',
    datos_factura: '',
    nit_proveedor: '',
    serie: '',
    numero_dt: '',
    centro_costo: '',
    tipo_pago: 'contado',
    fecha_pago_programada: '',
    forma_pago: 'transferencia',
    numero_bauche: ''
  });
  const isEfectivo = formData.forma_pago === 'efectivo';

  useEffect(() => {
    fetchStores();
    // Cargar NITs que requieren bitácora de vehículo
    axios.get(`${API}/vehiculo/config`)
      .then(r => setVehicleNits(r.data?.nits_requeridos || []))
      .catch(() => { /* silencioso, cae a []*/ });
  }, []);

  const normalizeNit = (nit) => (nit || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  const requiresVehicleForm = vehicleNits.includes(normalizeNit(formData.nit_proveedor));

  // Cuando cargan las tiendas, si el colaborador está restringido a una, auto-cargar
  // los proveedores de esa tienda y el centro de costo.
  useEffect(() => {
    if (isRestrictedToStore && stores.length > 0) {
      const myStore = stores.find(s => s.id === user.tienda_id);
      if (myStore) {
        fetchProviders(user.tienda_id);
        setFormData(prev => ({
          ...prev,
          tienda_id: myStore.id,
          centro_costo: myStore.nombre,
        }));
      }
    } else if (!isRestrictedToStore) {
      fetchProviders();
    }
  }, [stores]);

  const fetchProviders = async (tiendaId = null) => {
    try {
      let url = `${API}/providers`;
      if (tiendaId) {
        url += `?tienda_id=${tiendaId}`;
      }
      const response = await axios.get(url);
      setProviders(response.data);
    } catch (error) {
      toast.error('Error al cargar proveedores');
    }
  };

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API}/stores`);
      setStores(response.data);
    } catch (error) {
      toast.error('Error al cargar tiendas');
    }
  };

  const handleStoreChange = (storeId) => {
    setFormData(prev => ({ ...prev, tienda_id: storeId }));
    // Reload providers filtered by the selected store
    fetchProviders(storeId);
    // Clear selected provider when store changes
    setSelectedProvider(null);
    setFormData(prev => ({ 
      ...prev, 
      tienda_id: storeId,
      proveedor_id: '',
      nit_proveedor: '',
      banco: '',
      numero_cuenta: '',
      tipo_cuenta: ''
    }));
  };

  const handleProviderSelect = (providerId) => {
    const provider = providers.find(p => p.id === providerId);
    if (provider) {
      setSelectedProvider(provider);
      setFormData(prev => ({
        ...prev,
        proveedor_id: provider.id,
        nit_proveedor: provider.nit,
        banco: provider.banco || prev.banco,
        numero_cuenta: provider.numero_cuenta || prev.numero_cuenta,
        tipo_cuenta: provider.tipo_cuenta || prev.tipo_cuenta
      }));
    }
  };

  const filteredProviders = providers.filter(p => 
    p.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.nit.includes(searchTerm)
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.proveedor_id) {
      toast.error('Por favor selecciona un proveedor');
      return;
    }
    
    if (!formData.tienda_id) {
      toast.error('Por favor selecciona una tienda');
      return;
    }
    
    if (isEfectivo && !formData.numero_bauche.trim()) {
      toast.error('Ingresa el número de bauche');
      return;
    }

    if (formData.tipo_pago === 'credito' && !formData.fecha_pago_programada) {
      toast.error('Por favor ingresa la fecha de pago programada para crédito');
      return;
    }

    // Validar bitácora si aplica (aunque el backend también valida, damos feedback temprano)
    if (requiresVehicleForm) {
      const v = validateVehicleForm(vehicleData, { atCreation: true });
      if (!v.ok) { toast.error(v.error); return; }
    }
    
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        ...(isEfectivo
          ? { banco: '', numero_cuenta: '', titular_cuenta: '', tipo_cuenta: '' }
          : { numero_bauche: '' }),
        monto_depositar: parseFloat(formData.monto_depositar)
      };
      // Adjuntar bitácora en el mismo POST (atómico, evita pagos huérfanos)
      if (requiresVehicleForm) {
        submitData.vehiculo = vehicleData;
      }
      
      const response = await axios.post(`${API}/payments`, submitData);
      const paymentId = response.data.id;
      
      toast.success('Pago creado exitosamente');
      navigate(`/payments/${paymentId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear el pago');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40" data-testid="new-payment-page">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-button"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver al Panel
          </Button>
        </div>

        <Card className="max-w-4xl mx-auto p-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-6" data-testid="new-payment-title">
            Nuevo Registro de Pago
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="payment-form">
            {/* Provider Selection */}
            <div className="space-y-2">
              <Label htmlFor="proveedor">Proveedor *</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                <Input
                  placeholder="Buscar proveedor por nombre o NIT..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="provider-search"
                />
              </div>
              {searchTerm && (
                <div className="border rounded-md max-h-48 overflow-y-auto">
                  {filteredProviders.map(provider => (
                    <div
                      key={provider.id}
                      className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                      onClick={() => {
                        handleProviderSelect(provider.id);
                        setSearchTerm('');
                      }}
                    >
                      <div className="font-medium">{provider.nombre}</div>
                      <div className="text-sm text-slate-600">NIT: {provider.nit}</div>
                    </div>
                  ))}
                  {filteredProviders.length === 0 && (
                    <div className="p-3 text-center text-slate-600">No se encontraron proveedores</div>
                  )}
                </div>
              )}
              {selectedProvider && (
                <div className="p-3 bg-indigo-50 rounded-md">
                  <div className="font-medium text-indigo-900">{selectedProvider.nombre}</div>
                  <div className="text-sm text-[#1e3a8a]">NIT: {selectedProvider.nit}</div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre_envia">Nombre de quien envía *</Label>
                <Input
                  id="nombre_envia"
                  name="nombre_envia"
                  value={formData.nombre_envia}
                  onChange={handleChange}
                  required
                  data-testid="nombre-envia-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tienda">Tienda / Centro de Costo * <span className="text-xs text-slate-500">(Seleccione primero para ver proveedores disponibles)</span></Label>
                {isRestrictedToStore ? (
                  <div
                    className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
                    data-testid="tienda-locked-display"
                  >
                    <span>
                      {(stores.find(s => s.id === user.tienda_id)?.nombre) || 'Tu tienda asignada'}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Fijada</span>
                  </div>
                ) : (
                  <Select
                    value={formData.tienda_id || "none"}
                    onValueChange={(value) => {
                      const store = stores.find(s => s.id === value);
                      handleStoreChange(value === "none" ? "" : value);
                      setFormData(prev => ({
                        ...prev,
                        centro_costo: store ? store.nombre : ""
                      }));
                    }}
                  >
                    <SelectTrigger data-testid="tienda-select">
                      <SelectValue placeholder="Seleccionar tienda *" />
                    </SelectTrigger>
                    <SelectContent>
                      {stores.map(store => (
                        <SelectItem key={store.id} value={store.id}>
                          {store.nombre} {store.codigo && `(${store.codigo})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {formData.tienda_id && (
                  <p className="text-xs text-[#2c57c5]">✓ Mostrando proveedores de esta tienda + proveedores globales</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="forma_pago">Forma de Pago *</Label>
                <Select value={formData.forma_pago} onValueChange={(value) => setFormData(prev => ({...prev, forma_pago: value}))}>
                  <SelectTrigger data-testid="forma-pago-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {isEfectivo ? (
                <div className="space-y-2">
                  <Label htmlFor="numero_bauche">Número de Bauche *</Label>
                  <Input
                    id="numero_bauche"
                    name="numero_bauche"
                    value={formData.numero_bauche}
                    onChange={handleChange}
                    placeholder="Número del bauche de efectivo"
                    required
                    data-testid="numero-bauche-input"
                  />
                </div>
              ) : (
                <>
              <div className="space-y-2">
                <Label htmlFor="numero_cuenta">Número de Cuenta *</Label>
                <Input
                  id="numero_cuenta"
                  name="numero_cuenta"
                  value={formData.numero_cuenta}
                  onChange={handleChange}
                  required
                  data-testid="numero-cuenta-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="titular_cuenta">Titular de Cuenta *</Label>
                <Input
                  id="titular_cuenta"
                  name="titular_cuenta"
                  value={formData.titular_cuenta}
                  onChange={handleChange}
                  placeholder="Nombre del titular de la cuenta"
                  required
                  data-testid="titular-cuenta-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="banco">Banco *</Label>
                <Input
                  id="banco"
                  name="banco"
                  value={formData.banco}
                  onChange={handleChange}
                  required
                  data-testid="banco-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tipo_cuenta">Tipo de Cuenta *</Label>
                <Input
                  id="tipo_cuenta"
                  name="tipo_cuenta"
                  value={formData.tipo_cuenta}
                  onChange={handleChange}
                  placeholder="Ej: Ahorro, Corriente"
                  required
                  data-testid="tipo-cuenta-input"
                />
              </div>

                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="monto_depositar">Monto a Depositar (Q) *</Label>
                <Input
                  id="monto_depositar"
                  name="monto_depositar"
                  type="number"
                  step="0.01"
                  value={formData.monto_depositar}
                  onChange={handleChange}
                  required
                  data-testid="monto-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tipo_pago">Tipo de Pago *</Label>
                <Select value={formData.tipo_pago} onValueChange={(value) => setFormData(prev => ({...prev, tipo_pago: value}))}>
                  <SelectTrigger data-testid="tipo-pago-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contado">Contado</SelectItem>
                    <SelectItem value="credito">Crédito</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.tipo_pago === 'credito' && (
                <div className="space-y-2">
                  <Label htmlFor="fecha_pago_programada">Fecha de Pago Programada *</Label>
                  <Input
                    id="fecha_pago_programada"
                    name="fecha_pago_programada"
                    type="date"
                    value={formData.fecha_pago_programada}
                    onChange={handleChange}
                    required={formData.tipo_pago === 'credito'}
                    data-testid="fecha-pago-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="serie">Serie *</Label>
                <Input
                  id="serie"
                  name="serie"
                  value={formData.serie}
                  onChange={handleChange}
                  required
                  data-testid="serie-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="numero_dt">Número DT *</Label>
                <Input
                  id="numero_dt"
                  name="numero_dt"
                  value={formData.numero_dt}
                  onChange={handleChange}
                  required
                  data-testid="numero-dt-input"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="datos_factura">Datos de la Factura *</Label>
                <Input
                  id="datos_factura"
                  name="datos_factura"
                  value={formData.datos_factura}
                  onChange={handleChange}
                  placeholder="Descripción de la factura"
                  required
                  data-testid="datos-factura-input"
                />
              </div>
            </div>

            {/* Sub-formulario de vehículo (solo para proveedores de combustible) */}
            {requiresVehicleForm && (
              <div data-testid="vehicle-form-section">
                <VehicleForm
                  value={vehicleData}
                  onChange={setVehicleData}
                  tiendaId={formData.tienda_id}
                />
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
                data-testid="cancel-button"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-[#2c57c5] hover:bg-[#1e3a8a] text-white"
                data-testid="submit-payment-button"
              >
                {loading ? 'Guardando...' : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Guardar Pago
                  </>
                )}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
